import {api} from './api'
import {
  IBlackoutBulkRequest,
  IBlackoutBulkResponse,
  IBookingSlotsResponse,
  IFilters,
  IListResponse,
  ILocation
} from "../interfaces/locations";


export const locationsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getLocations: build.query<IListResponse<ILocation>, IFilters>({
      query: (params) => {
        return { url: `/locations`, params }
      },
      providesTags: (result) =>
          result
              ? [
                ...result.locations.map(({ id }) => ({
                  type: 'Locations' as const,
                  id
                })),
                { type: 'Locations', id: 'PARTIAL-LIST' }
              ]
              : [{ type: 'Locations', id: 'PARTIAL-LIST' }]
    }),
    getLocationsOptions: build.query<IListResponse<ILocation>, IFilters>({
      query: (params) => {
        return { url: `/locations/all`, params }
      },
    }),
    getLocationById: build.query<ILocation, string>({
      query: (id) => `/locations/${id}`,
      providesTags: (result, error, id) => [{ type: 'Locations', id }]
    }),
    createLocation: build.mutation<IListResponse<ILocation>, any>({
      query: (body) => ({
        url: '/locations',
        method: 'POST',
        body
      }),
      invalidatesTags: [{ type: 'Locations', id: 'PARTIAL-LIST' }]
    }),
    getLocationBookingSlots: build.query<IBookingSlotsResponse, { locationId: string, params?: IFilters }>({
      query: ({ locationId, params }) => ({
        url: `/locations/${locationId}/booking-slots`,
        params
      }),
    }),
    updateLocation: build.mutation<ILocation, { id: string, body: any }>({
      query: ({ id, body }) => ({
        url: `/locations/${id}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Locations', id },
        { type: 'Locations', id: 'PARTIAL-LIST' }
      ]
    }),
    updateLocationAvailability: build.mutation<any,{ locationId: string; availabilityId: string; body: Partial<any> }>({
        query: ({ locationId, availabilityId, body }) => ({
          url: `/locations/${locationId}/availability/${availabilityId}`,
          method: 'PUT',
          body,
        }),
    }),
    getLocationBlackouts: build.query<any, { locationId: string, params?: IFilters }>({
      query: ({ locationId, params }) => ({
        url: `/locations/${locationId}/blackouts`,
        params: {
          ...params,
          sort_by: params?.sort_by || 'date_start-desc'
        }
      }),
    }),
    getAvailableLocationBookingSlotsForRescheduling: build.query<IBookingSlotsResponse, { locationId: string, date: string }>({
      query: ({ locationId, date }) => ({
        url: `/locations/${locationId}/available-slots/${date}`,
      }),
    }),
    getAvailableSlots: build.query<any, { locationId: string, date: string }>({
      query: ({ locationId, date }) => `/locations/${locationId}/available-slots/${date}`,
    }),
    createBlackoutsBulk: build.mutation<IBlackoutBulkResponse, IBlackoutBulkRequest>({
      query: (body) => ({
        url: '/locations/blackouts-bulk',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Locations', id: 'LIST' }],
    }),
    updateBookingSlot: build.mutation<any, { locationId: string, bookingId: string, body: { date_start: string, date_end: string } }>({
      query: ({ locationId, bookingId, body }) => ({
        url: `/locations/${locationId}/booking-slots/${bookingId}`,
        method: 'PUT',
        body,
      }),
    }),
    deleteLocation: build.mutation<void, string>({
      query: (id) => ({
        url: `/locations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Locations', id },
        { type: 'Locations', id: 'PARTIAL-LIST' }
      ]
    }),
    deleteBlackout: build.mutation<void, { locationId: string; blackoutId: string }>({
      query: ({ locationId, blackoutId }) => ({
        url: `/locations/${locationId}/blackouts/${blackoutId}`,
        method: 'DELETE',
      }),
    }),
    getCustomerInsurance: build.query<any, string>({
      query: (customerId) => ({
        url: `/insurances/customer/${customerId}`,
        method: 'GET',
      }),
    }),
  })
})

export const {
  useGetLocationsQuery,
  useGetLocationsOptionsQuery,
  useGetLocationByIdQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useGetLocationBookingSlotsQuery,
  useUpdateLocationAvailabilityMutation,
  useGetLocationBlackoutsQuery,
  useGetAvailableLocationBookingSlotsForReschedulingQuery,
  useGetAvailableSlotsQuery,
  useCreateBlackoutsBulkMutation,
  useUpdateBookingSlotMutation,
  useDeleteLocationMutation,
  useDeleteBlackoutMutation,
  useGetCustomerInsuranceQuery
} = locationsApi
